import { Input, Spin } from 'antd';
import { useTranslation } from 'translations/hooks';
import { useTunnelScanner } from 'business/tunnel/pages/ring-scan/hooks/use-tunnel-scanner';
import { ScanSquare } from 'ui/components/scan-square';
import { NewSegmentDrawer } from 'business/tunnel/pages/ring-scan/components/new-segment-drawer';
import { AlertDrawer } from 'ui/components/alert-drawer';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { type Ring } from 'technical/db';

interface TunnelScannerProps {
  ring: Ring | null;
}

export interface TunnelScannerHandle {
  resetAndFocus: () => void;
}

export const TunnelScanner = forwardRef<
  TunnelScannerHandle,
  TunnelScannerProps
>(({ ring }, ref) => {
  const { t } = useTranslation();
  const {
    inputRef,
    searchValue,
    handleInputChange,
    handleKeyDown,
    isUpdating,
    newSegmentDrawerOpen,
    pendingSerialNumber,
    confirmNewSegment,
    cancelNewSegment,
    alertMessage,
    alertDrawerOpen,
    setAlertDrawerOpen,
    alertAction,
    resetAndFocus,
  } = useTunnelScanner(ring);

  useImperativeHandle(ref, () => ({ resetAndFocus }), [resetAndFocus]);

  useEffect(() => {
    if (!newSegmentDrawerOpen && !alertDrawerOpen) {
      resetAndFocus();
    }
  }, [newSegmentDrawerOpen, alertDrawerOpen, resetAndFocus]);

  return (
    <div className="flex flex-col">
      <ScanSquare>
        <Input
          placeholder={t('tunnel.scanner.placeholder')}
          size="large"
          ref={inputRef}
          value={searchValue}
          autoFocus
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isUpdating}
        />
        <div className="flex justify-center">
          {isUpdating ? <Spin size="small" /> : null}
        </div>
      </ScanSquare>

      <NewSegmentDrawer
        isOpen={newSegmentDrawerOpen}
        serialNumber={pendingSerialNumber}
        onConfirm={confirmNewSegment}
        onCancel={cancelNewSegment}
      />

      <AlertDrawer
        message={alertMessage}
        isOpen={alertDrawerOpen}
        setIsOpen={setAlertDrawerOpen}
        action={alertAction}
      />
    </div>
  );
});
